import React, { memo, useCallback } from 'react';
import { Input } from 'antd';

interface IDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Уход фокуса — окно задачи сохраняет описание сразу, не дожидаясь паузы. */
  onBlur?: () => void;
  /** Вставленные из буфера файлы (скриншоты) — во вложения задачи. */
  onPasteFiles?: (files: File[]) => void;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  ariaLabel?: string;
}

/**
 * Описание задачи — простой текст (ТЗ 0109).
 *
 * Всегда редактируемое поле, растущее по содержимому: без переключателя
 * «просмотр / правка», без панели разметки. Владелец: описание — это заметка,
 * а не документ. Ссылки в тексте кликабельны там, где описание ПОКАЗЫВАЕТСЯ
 * (`TaskDescription`), а не в самом поле.
 *
 * Скриншот, вставленный в поле, уходит во вложения, а не в текст: ссылка на
 * наш файл подписанная и живёт пять минут — в тексте она протухла бы.
 */
export const DescriptionField = memo(
  ({
    value,
    onChange,
    onBlur,
    onPasteFiles,
    placeholder,
    disabled = false,
    minRows = 4,
    ariaLabel,
  }: IDescriptionFieldProps) => {
    const onPaste = useCallback(
      (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!onPasteFiles) return;
        const files = Array.from(event.clipboardData?.files || []);
        if (!files.length) return;
        // Текст не трогаем: `preventDefault` только когда файлы реально есть.
        event.preventDefault();
        onPasteFiles(files);
      },
      [onPasteFiles],
    );

    return (
      <Input.TextArea
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoSize={{ minRows }}
        onPaste={onPaste}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  },
);

DescriptionField.displayName = 'DescriptionField';
